import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Copy, RefreshCw, Trash2, X } from 'lucide-react';

export interface CutierInvalidAddressEntry {
  id: string;
  raw: string;
  email: string;
  name?: string;
  reason: string;
}

interface CutierInvalidAddressesModalProps {
  isOpen: boolean;
  entries: CutierInvalidAddressEntry[];
  importedCount?: number;
  busy?: boolean;
  onClose: () => void;
  onUpdateEntry: (id: string, patch: Partial<Pick<CutierInvalidAddressEntry, 'email' | 'name'>>) => void;
  onRemoveEntry: (id: string) => void;
  onDismissAll: () => void;
  onRetry: () => void;
}

const inputClass =
  'w-full rounded-lg border border-slate-700 bg-[#0f1015] px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-[#E6C299] focus:outline-none';

const CutierInvalidAddressesModal: React.FC<CutierInvalidAddressesModalProps> = ({
  isOpen,
  entries,
  importedCount = 0,
  busy = false,
  onClose,
  onUpdateEntry,
  onRemoveEntry,
  onDismissAll,
  onRetry,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopyAll = async () => {
    const text = entries
      .map((e, i) => `${i + 1}. ${e.email}${e.name ? ` (${e.name})` : ''}\n   Original: ${e.raw}\n   → ${e.reason}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100000] flex animate-fade-in items-center justify-center bg-black/60 p-4 text-left backdrop-blur-sm">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl border border-slate-800 bg-[#0f1015] shadow-[0_0_50px_rgba(0,0,0,0.8)]">
        <div className="flex shrink-0 items-center justify-between rounded-t-2xl border-b border-slate-800 bg-[#15171e] p-6">
          <div>
            <div className="mb-1 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-red-500">
              <AlertTriangle className="h-3 w-3" />
              Warteschlange
            </div>
            <h2 className="text-xl font-bold uppercase tracking-widest text-white">
              Fehlerhafte Adressen
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {entries.length}{' '}
              {entries.length === 1 ? 'Eintrag wartet' : 'Einträge warten'} auf Korrektur — bleiben
              gespeichert, bis du sie löschst oder erneut absendest.
              {importedCount > 0 ? ` · ${importedCount} gültige Adresse(n) wurden übernommen` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-800 p-2 text-slate-400 transition-colors hover:bg-slate-700"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-6">
          {entries.length === 0 ? (
            <p className="text-center text-sm text-slate-400">Keine offenen Fehler mehr — alles erledigt.</p>
          ) : (
            entries.map((entry) => (
              <div
                key={entry.id}
                className="overflow-hidden rounded-xl border border-slate-800 bg-[#15171e] shadow-lg"
              >
                <div className="space-y-3 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      <label className="block">
                        <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-slate-500">
                          E-Mail korrigieren
                        </span>
                        <input
                          type="email"
                          value={entry.email}
                          onChange={(e) => onUpdateEntry(entry.id, { email: e.target.value })}
                          className={inputClass}
                          disabled={busy}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-slate-500">
                          Name (optional)
                        </span>
                        <input
                          type="text"
                          value={entry.name || ''}
                          onChange={(e) => onUpdateEntry(entry.id, { name: e.target.value })}
                          className={inputClass}
                          disabled={busy}
                        />
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveEntry(entry.id)}
                      disabled={busy}
                      className="mt-5 shrink-0 rounded-lg border border-slate-700 p-2 text-slate-400 transition-colors hover:border-red-800 hover:text-red-400 disabled:opacity-40"
                      aria-label="Eintrag entfernen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="rounded-lg border border-red-900/40 bg-red-950/20 px-3 py-2 text-xs text-slate-300">
                    <span className="font-mono text-slate-500">Original: {entry.raw}</span>
                    <div className="mt-1 flex items-start gap-2">
                      <span className="text-red-500">•</span>
                      <span>{entry.reason}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-b-2xl border-t border-slate-800 bg-[#15171e] p-4">
          <button
            type="button"
            onClick={onDismissAll}
            disabled={busy || entries.length === 0}
            className="text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-red-400 disabled:opacity-40"
          >
            Alle verwerfen
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void handleCopyAll()}
              disabled={entries.length === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-300 transition-colors hover:bg-slate-700 disabled:opacity-40"
            >
              <Copy className="h-3.5 w-3.5" />
              {copied ? 'Kopiert' : 'Liste kopieren'}
            </button>
            <button
              type="button"
              onClick={onRetry}
              disabled={busy || entries.length === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-[#E6C299] px-5 py-2 text-[10px] font-black uppercase tracking-widest text-black transition-colors hover:bg-white disabled:opacity-40"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
              {busy ? 'Prüfe…' : 'Korrigierte erneut senden'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CutierInvalidAddressesModal;

export const makeInvalidEntry = (
  raw: string,
  reason: string,
  email?: string,
  name?: string
): CutierInvalidAddressEntry => ({
  id:
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `inv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  raw,
  email: email ?? raw,
  name,
  reason,
});

export const INVALID_QUEUE_STORAGE_KEY = (monthKey: string) => `cutier_invalid_queue_${monthKey}`;

export const loadInvalidQueue = (monthKey: string): CutierInvalidAddressEntry[] => {
  try {
    const raw = localStorage.getItem(INVALID_QUEUE_STORAGE_KEY(monthKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CutierInvalidAddressEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e) => e && typeof e.id === 'string' && typeof e.email === 'string');
  } catch {
    return [];
  }
};

export const persistInvalidQueue = (monthKey: string, entries: CutierInvalidAddressEntry[]): void => {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(INVALID_QUEUE_STORAGE_KEY(monthKey));
    } else {
      localStorage.setItem(INVALID_QUEUE_STORAGE_KEY(monthKey), JSON.stringify(entries));
    }
  } catch {
    // ignore
  }
};
